import { cn } from '@forest/shared'
import { createMemo, For, Show, type Accessor, type JSX } from 'solid-js'
import coinIcon from '~/assets/currency/coin.svg'
import gemIcon from '~/assets/currency/gem.svg'
import { saleDiscountPercent } from '~/lib/effective-price'
import { intlLocale } from '~/lib/params'
import { m } from '~/paraglide/messages'
import type { CatalogueContent, CatalogueProduct } from '~/services/catalogue'
import { slotOwned, type CatalogueGridSlot } from '~/store/catalogue-grid'
import { CardFlip, type SlotReveal } from './card-flip'
import { CARD_SURFACE_CLASS } from './card-surface'
import { PLACEHOLDER_BAR_CLASS } from './placeholder-bar'
import {
  createCataloguePriceParts,
  freePriceLabel,
  StruckOriginalPrice,
  type PaidPriceParts,
} from './effective-price-parts'
import { UnlockCelebration } from './unlock-celebration'

const numberFormatter = new Intl.NumberFormat(intlLocale)

// A card grid's layout — wrapped rows of slots, centered, 8px apart — and
// one slot's width range, which together set how many columns fit: shared by
// every card grid and the boot skeleton's (GLOSSARY.md "Boot skeleton").
export const CATALOGUE_GRID_CLASS = 'flex flex-wrap justify-center gap-2 px-4'
const SLOT_ITEM_CLASS = 'relative min-w-[108px] max-w-[120px] flex-1'

// The card's footprint, shared by the hydrated card and its skeleton so the
// in-place fill does not shift layout: a 170px shell with the art, title and
// pill rows at fixed heights. The slot's <li> owns the width.
function CardShell(props: {
  art?: JSX.Element
  pill: JSX.Element
  title: JSX.Element
}) {
  return (
    <div
      class={cn(
        'flex h-[170px] w-full flex-col items-center gap-2 px-2 pt-2',
        CARD_SURFACE_CLASS,
      )}
    >
      <div class="flex w-full flex-col gap-1">
        <div class="h-20 w-full">{props.art}</div>
        <div class="flex h-8 w-full items-center">{props.title}</div>
      </div>
      {props.pill}
    </div>
  )
}

// The card's shell with a placeholder bar where the price pill will land:
// the design's tree-slot skeleton, which draws no title bar.
function CardPlaceholder() {
  return (
    <CardShell
      title={null}
      pill={<div class={cn(PLACEHOLDER_BAR_CLASS, 'h-[30px] w-full')} />}
    />
  )
}

// Placeholder card while its slot's product hydrates.
function SlotSkeleton(props: { gid: number }) {
  return (
    <div
      data-testid="catalogue-slot-skeleton"
      data-gid={props.gid}
      aria-hidden="true"
      class="animate-pulse"
    >
      <CardPlaceholder />
    </div>
  )
}

/**
 * A card grid of placeholders for the boot skeleton: the real grid's layout
 * and slot footprint, with no slot behind any card.
 */
export function CatalogueGridPlaceholder(props: {
  class?: string
  count: number
}) {
  return (
    <ul class={cn(CATALOGUE_GRID_CLASS, props.class)}>
      <For each={Array.from({ length: props.count })}>
        {() => (
          <li class={SLOT_ITEM_CLASS}>
            <CardPlaceholder />
          </li>
        )}
      </For>
    </ul>
  )
}

function OwnedPill() {
  return (
    <span
      data-testid="catalogue-owned"
      class="flex h-[30px] w-full items-center justify-center gap-0.5 rounded-full bg-[#DAEEE79C] px-2 text-sm font-semibold text-[#7BABA7]"
    >
      <svg
        role="img"
        aria-label={m.owned_tag()}
        width="22"
        height="22"
        viewBox="0 0 22 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M5.49341 11L9.16012 14.6667L16.5065 7.33325"
          stroke="#7BABA7"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  )
}

// The card's price: currency icon, struck original during a sale, and the
// effective price — or "Free" alone for a free product (GLOSSARY.md "Free
// product").
function PricePill(props: { paid: PaidPriceParts | null }) {
  return (
    <span
      data-testid="catalogue-price"
      data-amount={props.paid?.amount}
      data-original={props.paid?.original ?? undefined}
      class="flex h-[30px] w-full items-center justify-center gap-1 rounded-full bg-white/60 px-2 text-sm font-semibold text-[#1FA880]"
    >
      <Show when={props.paid} fallback={freePriceLabel()}>
        {(paid) => (
          <>
            <img
              alt=""
              class="size-[22px]"
              src={paid().currency === 'coin' ? coinIcon : gemIcon}
            />
            <StruckOriginalPrice original={paid().original} />
            {numberFormatter.format(paid().amount)}
          </>
        )}
      </Show>
    </span>
  )
}

// The card's percentage-off badge (GLOSSARY.md "Sale tag"), over the art's
// top-left corner. English only, by decision (2026-09-02).
function SaleTag(props: { percent: number }) {
  return (
    <span
      data-testid="catalogue-sale-tag"
      class="absolute left-3 top-3 flex h-5 items-center rounded-full bg-[#BEEBF5] px-2 text-[10px] font-bold text-[#105F71]"
    >
      {props.percent}% off
    </span>
  )
}

// One Classic-style card slot (GLOSSARY.md "Daily pick collection" — the
// Featured tab's picks follow the Classic card's rules, so the Classic and
// Daily Picks grids render the same slot): skeleton while pending, the card
// once hydrated, nothing once omitted.
export function CatalogueSlot(props: {
  // Locks the card out of taps and marks it disabled for assistive tech —
  // the Daily Picks grid's pending state during a refresh (ADR 0032).
  disabled?: boolean
  // Ownership and celebration are keyed by (content, gid): tree and
  // land-skin gids are separate namespaces (GLOSSARY.md "Owned land skin").
  isCelebrating?: (content: CatalogueContent, gid: number) => boolean
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  onSelect: (product: CatalogueProduct) => void
  // The refresh reveal's face for this slot (GLOSSARY.md "Refresh reveal"),
  // passed by the Daily Picks grid alone.
  reveal?: SlotReveal
  // The slot's list item, while it is mounted.
  ref?: (element: HTMLLIElement) => void
  serverNow: Accessor<number>
  slot: CatalogueGridSlot
}) {
  // The card face, built on demand so whichever branch below mounts builds
  // its own.
  const cardFace = () => (
    <Show
      when={props.slot.product}
      fallback={<SlotSkeleton gid={props.slot.gid} />}
    >
      {(product) => {
        const isOwned = () => slotOwned(props.slot, props.isOwned)
        // The pill and the tag share one effective-price subscription.
        const paid = createCataloguePriceParts(
          () => product().price,
          () => props.serverNow(),
        )
        // No tag on an owned card — nothing left to buy — nor on a free
        // one, which has no sale (GLOSSARY.md "Sale tag", "Free product").
        const saleTagPercent = createMemo(() => {
          const parts = paid()
          return isOwned() || !parts ? null : saleDiscountPercent(parts)
        })
        return (
          <>
            <button
              type="button"
              data-testid="catalogue-product"
              data-content={props.slot.content}
              data-gid={props.slot.gid}
              disabled={props.disabled}
              onClick={() => props.onSelect({ ...product(), owned: isOwned() })}
              class="relative w-full"
            >
              <CardShell
                art={
                  <img
                    alt=""
                    src={product().productImage.src}
                    class="size-full object-contain"
                  />
                }
                title={
                  <span
                    data-testid="catalogue-title"
                    class="w-full text-center text-xs font-medium leading-[14px] text-ink"
                  >
                    {product().title}
                  </span>
                }
                pill={
                  <Show when={isOwned()} fallback={<PricePill paid={paid()} />}>
                    <OwnedPill />
                  </Show>
                }
              />
              <Show when={saleTagPercent()}>
                {(percent) => <SaleTag percent={percent()} />}
              </Show>
            </button>
            <Show
              when={props.isCelebrating?.(props.slot.content, props.slot.gid)}
            >
              <UnlockCelebration />
            </Show>
          </>
        )
      }}
    </Show>
  )
  return (
    <Show when={props.slot.status !== 'omitted'}>
      <li ref={(element) => props.ref?.(element)} class={SLOT_ITEM_CLASS}>
        {/* Only the Daily Picks grid's slots turn: they carry a reveal and
            mount their two faces, while a Classic card is the face alone. */}
        <Show when={props.reveal} fallback={cardFace()}>
          {(reveal) => <CardFlip reveal={reveal()}>{cardFace()}</CardFlip>}
        </Show>
      </li>
    </Show>
  )
}
